import videojs from 'video.js';
import type Player from 'video.js/dist/types/player';
import { getIsLastNumOfSeconds, setPersistedPlayerSettings } from '../utils';

interface TextTrack {
  kind: string;
  mode: string;
  language?: string;
  label?: string;
}

interface TextTrackList {
  length: number;
  [index: number]: TextTrack;
  addEventListener: (event: string, callback: () => void) => void;
  removeEventListener: (event: string, callback: () => void) => void;
}

export default function persistedSettingsPlugin(
  this: Player,
  { playerID }: { playerID: string }
) {
  const player = videojs(playerID);
  let tracksChangeListener: (() => void) | null = null;

  const getShowingTrackInfo = (
    tracks: TextTrackList
  ): {
    captionsEnabled: boolean;
    selectedTrack?: { language?: string; label?: string };
  } => {
    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i];
      if (
        (track.kind === 'captions' || track.kind === 'subtitles') &&
        track.mode === 'showing'
      ) {
        return {
          captionsEnabled: true,
          selectedTrack: {
            language: track.language,
            label: track.label,
          },
        };
      }
    }
    return { captionsEnabled: false };
  };

  const saveAllSettings = () => {
    const tracks = player.textTracks() as unknown as TextTrackList;
    const { captionsEnabled, selectedTrack } = getShowingTrackInfo(tracks);

    setPersistedPlayerSettings(
      player.volume(),
      player.muted(),
      captionsEnabled,
      selectedTrack
    );
  };

  player.on('timeupdate', () => {
    if (!getIsLastNumOfSeconds(player)) return;
    saveAllSettings();
  });

  player.on('volumechange', () => {
    if (getIsLastNumOfSeconds(player) || player?.currentTime() === 0) return;
    saveAllSettings();
  });

  player.on('loadedmetadata', () => {
    const tracks = player.textTracks() as unknown as TextTrackList;

    if (tracksChangeListener) {
      tracks.removeEventListener('change', tracksChangeListener);
    }

    tracksChangeListener = saveAllSettings;
    tracks.addEventListener('change', tracksChangeListener);
  });

  player.on('dispose', () => {
    if (tracksChangeListener) {
      const tracks = player.textTracks() as unknown as TextTrackList;
      tracks.removeEventListener('change', tracksChangeListener);
      tracksChangeListener = null;
    }
  });
}
